import {
  Button,
  Container,
  Flex,
  Box,
  Text,
  VStack,
  useBreakpointValue,
  Image,
  Spinner,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { Navbar } from "./Navbar";
import { Sidebar } from "./Sidebar";
import { Navigate, Routes, Route } from "react-router-dom";
import Events from "./Events/Events";
import Bundles from "./Bundles/Bundles";

import Team from "./Team";
import Settings from "./Settings";

import EventDetail from "./EventDetail";
import BundleDetail from "./Bundles/BundleDetail";

import { ModalType } from "../utils";
import FeedManager from "./FeedManager/FeedManager";
import actions from "../actions";
import { KYDOrganizationUser } from "@common/interfaces/KYDUser";
import logo from "../darksmall.png";
import ChangeOrganizationModal from "./ChangeOrganizationModal";
import { CognitoUser } from "amazon-cognito-identity-js";
import Detail from "./EventDetail/Nav/Detail";
import GuestlistNavSection from "./EventDetail/Nav/Guestlist/GuestlistNavSection";
import ReportingNavSection from "./EventDetail/Nav/Reporting/ReportingNavSection";
import SettingsNavSection from "./EventDetail/Nav/Settings/SettingsNavSection";
import MarketingNavSection from "./EventDetail/Nav/Marketing/MarketingNavSection";
import AtteendeeNavSection from "./EventDetail/Nav/Attendees/AttendeesNavSection";
import WaitlistNavSection from "./EventDetail/Nav/Waitlist/WaitlistNavSection";
import AnalyticsNavSection from "./EventDetail/Nav/Analytics/AnalyticsNavSection";
import PayoutsNavSection from "./EventDetail/Nav/Payouts/PayoutsNavSection";
import Dashboard from "./Dashboard/Dashboard";
import PayoutsDashboard from "./Payouts/Dashboard";
import PayoutDetails from "./Payouts/PayoutDetails";
export const PageContainer = ({
  children,
  px = 4,
  py = 8,
}: {
  children: React.ReactNode;
  px?: number;
  py?: number;
}) => (
  <Container
    maxW={"6xl"}
    px={px}
    py={py}
    flex="1"
    height="100%"
    overflowY="scroll"
  >
    {children}
  </Container>
);

const EventManager = ({
  cognitoUser,
  setModalType,
}: {
  cognitoUser: CognitoUser;
  setModalType: (state: ModalType) => void;
}) => {
  const isDesktop = useBreakpointValue({ base: false, md: true });
  const [currentUser, setCurrentUser] = useState<KYDOrganizationUser>();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    document.title = "Manager";
    setIsLoading(true);
    const checkAccessToken = async (_me: KYDOrganizationUser) => {
      const accessToken = await actions.ADMIN.fetchAccessToken();
      if (accessToken) {
        const payload = accessToken.decodePayload();
        if (
          _me &&
          _me.updated_at &&
          payload["iat"] < new Date(_me.updated_at).getTime() / 1000
        ) {
          await refreshAccessToken();
        }
      }
    };

    const fetchMe = async () => {
      try {
        const me = await actions.ADMIN.fetchMe();
        await checkAccessToken(me);
        setCurrentUser(me);
      } catch (err) {
        //dialog({ text: err.message });
      }

      setIsLoading(false);
    };
    fetchMe();
  }, [cognitoUser]);

  const refreshAccessToken = useCallback(async () => {
    await actions.ADMIN.refreshAccessToken();
  }, [currentUser]);

  const switchOrganization = useCallback(
    async (organization_id: string) => {
      setIsLoading(true);
      try {
        await actions.ADMIN.updateMe(organization_id);
        await refreshAccessToken();
        window.location.reload();
      } catch (err) {
        setIsLoading(false);
      }
    },
    [currentUser]
  );

  return currentUser ? (
    <Flex as="section" direction={{ base: "column", md: "row" }} height="100vh">
      <ChangeOrganizationModal
        currentUser={currentUser}
        isOpen={isOpen}
        onCancel={() => setIsOpen(false)}
        onSave={switchOrganization}
        isLoading={isLoading}
      />
      {isDesktop ? (
        <Sidebar
          currentUser={currentUser}
          onOrganizationClick={() => setIsOpen(true)}
          scopes={currentUser?.scopes}
        />
      ) : (
        <Navbar
          currentUser={currentUser}
          onOrganizationClick={() => setIsOpen(true)}
          scopes={currentUser?.scopes}
        />
      )}

      <Box w="100%" height="100%">
        <Routes>
          <Route
            path="/settings"
            element={
              <PageContainer>
                <Settings />
              </PageContainer>
            }
          />
          <Route
            path="/bio"
            element={
              <PageContainer>
                <FeedManager />
              </PageContainer>
            }
          />
          {currentUser?.scopes?.includes("organizations/write") ||
          currentUser?.scopes?.includes("organizations/payouts:write") ||
          currentUser?.scopes?.includes("organizations/payouts:read") ? (
            <>
              <Route
                path="/payouts"
                element={
                  <PageContainer>
                    <PayoutsDashboard />
                  </PageContainer>
                }
              />
              <Route
                path="/payouts/:payout_id"
                element={
                  <PageContainer>
                    <PayoutDetails />
                  </PageContainer>
                }
              />
            </>
          ) : null}

          {currentUser?.scopes?.includes("organizations/write") ||
          currentUser?.scopes?.includes("organizations/read") ||
          currentUser?.scopes?.includes("organizations/users:read") ? (
            <Route
              path="/team"
              element={
                <PageContainer>
                  <Team
                    scopes={currentUser?.scopes}
                    currentUser={currentUser}
                  />
                </PageContainer>
              }
            />
          ) : null}
          <Route path="/events">
            <Route
              index
              element={
                <PageContainer>
                  <Events />
                </PageContainer>
              }
            />
            <Route
              path=":event_id"
              element={<EventDetail setAppModalType={setModalType} />}
            >
              <Route index element={<Detail />} />
              <Route path="guestlist" element={<GuestlistNavSection />} />
              <Route path="attendees" element={<AtteendeeNavSection />} />
              <Route path="waitlist" element={<WaitlistNavSection />} />
              <Route path="marketing" element={<MarketingNavSection />} />
              <Route path="analytics" element={<AnalyticsNavSection />} />
              <Route path="reporting" element={<ReportingNavSection />} />
              <Route path="settings" element={<SettingsNavSection />} />
              <Route path="payouts" element={<PayoutsNavSection />} />
            </Route>
          </Route>
          {currentUser?.scopes?.includes("organizations/overview:read") ||
          currentUser?.scopes?.includes("organizations/write") ? (
            <Route path="/dashboard" element={<Dashboard />} />
          ) : null}
          <Route path="/bundles">
            <Route
              index
              element={
                <PageContainer>
                  <Bundles currentUser={currentUser} />
                </PageContainer>
              }
            />
            <Route path=":bundle_id" element={<BundleDetail />}>
              <Route index element={<Detail />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to={"/events"} />} />
        </Routes>
      </Box>
    </Flex>
  ) : (
    <VStack h="100vh" justify={"center"}>
      <Image src={logo} w="100px" />
      {isLoading ? (
        <Spinner color="black" />
      ) : (
        <>
          <Text>Please sign in to access your dashboard</Text>
          <Button
            bg="black"
            color="white"
            onClick={() => setModalType(ModalType.SIGN_IN_PHONE_NUMBER)}
          >
            Sign In
          </Button>
        </>
      )}
    </VStack>
  );
};

export default EventManager;
